import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import {
  Add,
  ApartmentRounded,
  DeleteOutline,
  GroupsRounded,
  Search,
  ShieldRounded,
} from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import { Loader } from "components/Loader";
import { CustomAutoComplete } from "components/CustomComponents";
import { GlobalFieldLabel } from "components/DesignSystem/sharedUiPatterns";
import { useFetch } from "services/hooks/useFetch";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { notifyAccessChanged } from "services/utils/companyAccess";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  atleastOneCharacterWithSpace,
  specialCharacterRestrict,
} from "services/constants/patterns";
import { designColors, designFontFamily } from "services/constants/designTokens";
import { roleAndGroupUi, statusChip } from "../ui";
import { roleKitType, tealCheckboxSx } from "../UserRole/roleUiKit";
import { UserGroupUserPicker } from "./UserGroupUserPicker";
import { buildGroupUsersPayload, normalizeGroupMembers } from "./userGroupPayload";
import { normalizeDepartmentOptions, isGroupMember, isActiveRecord, membershipUserId } from "./membershipRules";
import {
  DepartmentMultiSelect,
  getDepartmentScopeValue,
  hasValidDepartmentScope,
} from "./DepartmentMultiSelect";
import {
  GroupIcon,
  MEMBER_PREVIEW,
  MoreRow,
  UserAvatar,
  getGroupStatus,
  groupAssignmentsByCompany,
} from "./groupUtils";

const NAME_MAX = 100;
const DESCRIPTION_MAX = 250;
const DEFAULT_SCOPE = { scope: "current", departments: [] };

const fieldSx = {
  ...roleAndGroupUi.textField,
  "& .MuiInputBase-input::placeholder": { color: "#94A3B8", opacity: 1 },
};

const headCellSx = {
  fontSize: 13,
  fontWeight: 700,
  fontFamily: designFontFamily,
  color: designColors.slate800,
  backgroundColor: designColors.slateBg,
  borderBottom: `1px solid ${designColors.slate200}`,
  py: 1.1,
  px: 1.5,
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  fontSize: 13.5,
  fontFamily: designFontFamily,
  color: designColors.slate800,
  borderBottom: `1px solid ${designColors.slate100}`,
  py: 1,
  px: 1.5,
  verticalAlign: "middle",
};

// Company column: hidden on phones (shown under the name there instead).
// Collapsed (620px drawer): fixed widths that fit. Expanded full view:
// proportional widths so Name does not swallow all the extra space.
const companyColumnSx = (expanded) => ({
  width: expanded ? "22%" : 120,
  display: { xs: "none", sm: "table-cell" },
});
const departmentColumnWidth = (expanded) => (expanded ? "28%" : { xs: 150, sm: 180 });

const tableBoxSx = {
  border: `1px solid ${designColors.slate200}`,
  borderRadius: "10px",
  overflowX: "auto",
};

// Outlined "+ Add Company" / "+ Add Users" buttons on the right of each
// section heading, in the app primary (teal).
const addSectionButtonSx = {
  flexShrink: 0,
  minHeight: 40,
  px: 2,
  borderRadius: "8px",
  textTransform: "none",
  whiteSpace: "nowrap",
  fontFamily: designFontFamily,
  fontSize: 14,
  fontWeight: 600,
  color: designColors.tealDark,
  borderColor: designColors.tealDark,
  backgroundColor: designColors.white,
  "&:hover": { borderColor: designColors.tealDark, backgroundColor: designColors.tealTint },
  "&.Mui-disabled": { color: "#94A3B8", borderColor: "#E2E8F0" },
};

const deleteButtonSx = {
  color: "#DC2626",
  "&:hover": { backgroundColor: "rgba(220, 38, 38, 0.08)" },
};

const autocompleteSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
    borderRadius: "8px",
    fontSize: 13.5,
    fontFamily: designFontFamily,
    backgroundColor: designColors.white,
    "& fieldset": { borderColor: designColors.borderInput },
    "&:hover fieldset": { borderColor: designColors.borderInputHover },
    "&.Mui-focused fieldset": { borderColor: "#0D9488" },
  },
  "& .MuiInputBase-input::placeholder": { color: "#94A3B8", opacity: 1 },
};

const errorText = (message) =>
  message ? (
    <Mui.Typography sx={{ mt: 0.5, fontSize: 12.5, color: "#D32F2F" }}>{message}</Mui.Typography>
  ) : null;

const SectionTitle = ({ icon, iconColor = "#0F766E", title, subtitle, action }) => (
  <Mui.Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={1.5}
    alignItems={{ xs: "flex-start", sm: "center" }}
    justifyContent="space-between"
    sx={{ mb: 2 }}
  >
    <Mui.Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
      {icon ? (
        <Mui.Box sx={{ display: "flex", color: iconColor, "& svg": { fontSize: 28 } }}>
          {icon}
        </Mui.Box>
      ) : null}
      <Mui.Box sx={{ minWidth: 0 }}>
        <Mui.Typography sx={roleKitType.sectionTitle}>{title}</Mui.Typography>
        {subtitle ? <Mui.Typography sx={roleKitType.sectionSubtitle}>{subtitle}</Mui.Typography> : null}
      </Mui.Box>
    </Mui.Stack>
    {action}
  </Mui.Stack>
);

// Each form section is a white card on the panel's grey body, as in the
// mockup.
const sectionCardSx = {
  backgroundColor: designColors.white,
  border: `1px solid ${designColors.slate200}`,
  borderRadius: "12px",
  p: { xs: 2, sm: 2.5 },
};

// Keep the teal border and ring visible for the entire Add Users session.
const pickerActiveCardSx = {
  ...sectionCardSx,
  borderColor: designColors.tealDark,
  boxShadow: "0 0 0 3px rgba(15, 118, 110, 0.25)",
};

// Long lists show a preview and a "N more" row that expands them.
const COMPANY_PREVIEW = 3;


// Company rows are stacked flex rows (not a fixed-width table) so the section
// fits the 620px drawer without a horizontal scrollbar, as in the mockup.
const companyListSx = {
  border: `1px solid ${designColors.slate200}`,
  borderRadius: "10px",
  overflow: "hidden",
};
const companyRowSx = {
  display: "flex",
  flexWrap: { xs: "wrap", sm: "nowrap" },
  alignItems: "center",
  gap: 1.5,
  px: 1.5,
  py: 1.25,
  borderBottom: `1px solid ${designColors.slate100}`,
  "&:last-of-type": { borderBottom: "none" },
};
const ROLE_CHIP_FONT_SIZE = 12.5;
const roleChipSx = {
  height: 26,
  // Full names: never shrink or truncate. How many chips are shown is
  // decided by measuring (see RoleChipSelect), not by squeezing them.
  flex: "0 0 auto",
  maxWidth: "none",
  borderRadius: "6px",
  fontSize: ROLE_CHIP_FONT_SIZE,
  fontWeight: 500,
  fontFamily: designFontFamily,
  color: designColors.slate800,
  backgroundColor: "#F1F5F9",
  border: `1px solid ${designColors.slate200}`,
  "& .MuiChip-deleteIcon": { fontSize: 15, color: designColors.slate600 },
};
const moreChipSx = {
  ...roleChipSx,
  color: designColors.tealDark,
  fontWeight: 600,
  backgroundColor: designColors.tealTint,
  borderColor: "#CDEDE4",
};

let measureCanvas;
const textWidth = (text, weight) => {
  if (typeof document === "undefined") return String(text).length * 7;
  measureCanvas = measureCanvas || document.createElement("canvas");
  const context = measureCanvas.getContext("2d");
  if (!context) return String(text).length * 7;
  context.font = `${weight} ${ROLE_CHIP_FONT_SIZE}px ${designFontFamily}`;
  return context.measureText(String(text)).width;
};
// Rendered widths of a small MUI Chip incl. its 3px side margins:
// label padding 2x8 + border 2 + margins 6, plus the delete icon (~14).
const roleChipWidth = (name) => Math.ceil(textWidth(name, 500)) + 38;
const moreChipWidth = (count) => Math.ceil(textWidth(`+${count} more`, 600)) + 24;
// Autocomplete input padding (left ~6, right ~39 for the popup arrow) plus
// room left for typing.
const ROLE_FIELD_RESERVED = 6 + 39 + 20;

// How many selected roles fit on one line at full width, leaving room for
// the "+N more" chip when some are left over. Always at least one.
const fitRoleChips = (names, fieldWidth) => {
  if (!fieldWidth) return Math.min(names.length, 2);
  const available = fieldWidth - ROLE_FIELD_RESERVED;
  let used = 0;
  for (let index = 0; index < names.length; index += 1) {
    const next = used + roleChipWidth(names[index]);
    const remaining = names.length - index - 1;
    const needed = next + (remaining > 0 ? moreChipWidth(remaining) : 0);
    if (needed > available) return Math.max(1, index);
    used = next;
  }
  return names.length;
};

// Role picker for one company row. Selected roles show as full-width chips
// on one line -- as many as fit -- and the rest fold into a "+N more" chip
// (hover lists them). The dropdown still lists every role with a checkbox.
const RoleChipSelect = ({ options, value, onChange, hasError }) => {
  const rootRef = useRef(null);
  const [fieldWidth, setFieldWidth] = useState(0);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    const update = () => setFieldWidth(node.getBoundingClientRect().width);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const visibleCount = fitRoleChips(
    value.map((option) => option?.role_name ?? ""),
    fieldWidth,
  );

  return (
    <Mui.Autocomplete
      ref={rootRef}
      multiple
      size="small"
      disableCloseOnSelect
      // No clear-all "x": it took chip space; chips are removed one by one or
      // unticked in the dropdown.
      disableClearable
      // Open list capped at ~5 roles, the rest scroll (2026-10-01, user
      // request) -- same ListboxProps cap the company field's
      // CustomAutoComplete uses.
      ListboxProps={{ style: { maxHeight: 200 } }}
      options={options}
      value={value}
      onChange={(event, selected) => onChange(selected)}
      getOptionLabel={(option) => option?.role_name ?? ""}
      isOptionEqualToValue={(option, selected) => String(option?.role_id) === String(selected?.role_id)}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option?.role_id}>
          <Mui.Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5, ...tealCheckboxSx }} />
          {option?.role_name}
        </li>
      )}
      renderTags={(selected, getTagProps) => {
        const hidden = selected.length - visibleCount;
        // A single role wider than the whole field is the only case that
        // still truncates (with the full name on hover).
        const onlyOne = visibleCount === 1;
        return [
          ...selected.slice(0, visibleCount).map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            const chip = (
              <Mui.Chip
                key={key}
                {...tagProps}
                size="small"
                label={option?.role_name}
                sx={onlyOne ? { ...roleChipSx, flex: "0 1 auto", minWidth: 0 } : roleChipSx}
              />
            );
            return onlyOne ? (
              <Mui.Tooltip key={key} arrow slotProps={tooltipStyleProps} title={option?.role_name ?? ""}>
                {chip}
              </Mui.Tooltip>
            ) : (
              chip
            );
          }),
          hidden > 0 ? (
            <Mui.Tooltip
              key="more"
              arrow
              slotProps={tooltipStyleProps}
              title={selected
                .slice(visibleCount)
                .map((option) => option?.role_name)
                .join(", ")}
            >
              <Mui.Chip size="small" label={`+${hidden} more`} sx={moreChipSx} />
            </Mui.Tooltip>
          ) : null,
        ];
      }}
      renderInput={(params) => (
        <Mui.TextField {...params} error={hasError} placeholder={value.length ? "" : "Select role(s)"} />
      )}
      sx={{
        ...autocompleteSx,
        width: "100%",
        minWidth: 0,
        // One line only.
        "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap", overflow: "hidden" },
        "& .MuiAutocomplete-input": { minWidth: "16px !important", flex: "1 0 16px" },
      }}
    />
  );
};

// Normalizes a group member from the GET payload (scope label, {id, name}
// departments) into the shape the form and DepartmentMultiSelect use.
const normalizeMember = (user, key) => ({
  key,
  id: membershipUserId(user),
  user_id: user?.user_id,
  user_code: user?.user_code,
  employee_id: user?.employee_id,
  name: user?.name ?? user?.user_name,
  email: user?.email,
  image_url: user?.image_url,
  company: user?.company,
  company_id: user?.company_id,
  home_company_id: user?.home_company_id,
  home_company: user?.home_company,
  scope: getDepartmentScopeValue(user?.scope) || "additional",
  departments: normalizeDepartmentOptions((user?.departments ?? []).map((dept) =>
    typeof dept === "object" ? dept : { id: dept })),
  isNew: false,
});

// initialExpanded: open straight in the full view (Edit clicked from an
// expanded View group).
// onExpandedChange: reports every side panel / full view switch, so the
// View panel can come back in the same mode.
export const UserGroupForm = ({
  mode = "edit",
  group,
  onClose,
  onSaved,
  initialExpanded = false,
  onExpandedChange,
}) => {
  const isEdit = mode === "edit";
  const { domain } = useSelector((state) => state.tokenReducer);

  const { result: companyList, loading: companiesLoading } = useFetch(`${domain}get-active-company/`);
  const { result: roleList } = useFetch(`${domain}product-control/roles/summary/`);
  const [departmentList, setDepartmentList] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [departmentsError, setDepartmentsError] = useState("");
  const [detailsError, setDetailsError] = useState("");

  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(isEdit);
  const [initialised, setInitialised] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("Active");
  const [companies, setCompanies] = useState([]);
  const [members, setMembers] = useState([]);
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  // Users ticked in the inline Add Users picker, not yet added to the group.
  const [pickedUsers, setPickedUsers] = useState([]);
  // Controlled here (instead of inside GlobalSidePanel) so the users table
  // can size its columns for the expanded full view.
  const [panelExpanded, setPanelExpanded] = useState(Boolean(initialExpanded));
  useEffect(() => {
    onExpandedChange?.(panelExpanded);
    // onExpandedChange is a plain setter from the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelExpanded]);
  const [showAllCompanies, setShowAllCompanies] = useState(false);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [bulkScope, setBulkScope] = useState(DEFAULT_SCOPE);
  const [errors, setErrors] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const keyRef = useRef(0);
  const nextKey = () => {
    keyRef.current += 1;
    return `row-${keyRef.current}`;
  };

  useEffect(() => {
    if (!isEdit || !group?.id) return undefined;
    let cancelled = false;
    customGetAPI(`${domain}create-assign/user-group/?id=${group.id}`)
      .then((res) => {
        if (cancelled) return;
        if (!res?.ok) throw new Error(res?.message || "Couldn't load this user group. Reopen the form to retry.");
        if (!Array.isArray(res.responseDetails?.users) || !Array.isArray(res.responseDetails?.assignments)) {
          throw new Error("Couldn't load complete user group details. Reopen the form to retry.");
        }
        setDetails(res.responseDetails);
      })
      .catch((error) => { if (!cancelled) setDetailsError(error.message); })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [domain, group?.id, isEdit]);

  const companies_ = useMemo(() => (Array.isArray(companyList) ? companyList : []).filter(isActiveRecord), [companyList]);
  const ready = !companiesLoading && !detailsLoading && !detailsError;
  const supportsStatus = isEdit;

  useEffect(() => {
    if (!ready || initialised) return;
    setInitialised(true);
    if (!isEdit) return;
    const source = details ?? group ?? {};
    setName(source?.name ?? "");
    setDescription(source?.description ?? "");
    setStatus(getGroupStatus(source));
    setCompanies(
      groupAssignmentsByCompany(source?.assignments).map((company) => ({
        ...company,
        key: nextKey(),
        isNew: false,
      })),
    );
    setMembers(
      normalizeGroupMembers(source?.users ?? []).map((user) =>
        normalizeMember(
          {
            ...user,
            company: companies_.find((company) => String(company.id) === String(user.company_id))?.companyname ?? user.company,
            home_company: (user.home_company_id != null
              ? companies_.find((company) => String(company.id) === String(user.home_company_id))?.companyname
              : undefined) ?? user.home_company ?? user.home_company_name,
          },
          nextKey(),
        ),
      ),
    );
  }, [companies_, details, group, initialised, isEdit, ready]);

  // Original memberships may be added again after removal. Eligibility for
  // new members is loaded by the picker for its selected membership company.
  const userPool = useMemo(() => {
    const pool = [];
    normalizeGroupMembers(details?.users ?? []).forEach((user) => {
      if (!isGroupMember(user, pool)) {
        pool.push({
          id: membershipUserId(user),
          user_id: user?.user_id,
          user_code: user?.user_code,
          employee_id: user?.employee_id ?? user?.user_code,
          name: user?.name ?? user?.user_name,
          email: user?.email,
          image_url: user?.image_url,
          company_id: user?.company_id,
          company: user?.company,
          home_company_id: user?.home_company_id,
        });
      }
    });
    return pool;
  }, [details?.users]);

  useEffect(() => {
    let cancelled = false;
    setDepartmentList([]);
    setDepartmentsError("");
    setDepartmentsLoading(true);
    customGetAPI(`${domain}department/`).then((res) => {
      if (!res?.ok) throw new Error("Couldn't load departments. Please reopen the form to retry.");
      if (!cancelled) setDepartmentList(normalizeDepartmentOptions(res.responseDetails));
    }).catch((error) => {
      if (!cancelled) setDepartmentsError(error.message);
    }).finally(() => {
      if (!cancelled) setDepartmentsLoading(false);
    });
    return () => { cancelled = true; };
  }, [domain]);

  const roleOptions = useMemo(
    () =>
      (Array.isArray(roleList) ? roleList : []).filter(isActiveRecord).map((role) => ({
        role_id: role?.role_id,
        role_name: role?.role_name,
      })),
    [roleList],
  );

  const clearRowError = (key, field) =>
    setRowErrors((prev) => ({ ...prev, [key]: { ...prev[key], [field]: "" } }));

  // ---- Companies & roles --------------------------------------------------

  const availableCompanies = (rowKey) => {
    const taken = new Set(
      companies.filter((row) => row.key !== rowKey).map((row) => String(row.company_id)),
    );
    return companies_.filter((company) => !taken.has(String(company?.id)));
  };

  const updateCompany = (key, patch) =>
    setCompanies((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const addCompany = () => {
    setCompanies((prev) => [
      ...prev,
      { key: nextKey(), company_id: null, company_name: "", roles: [], isNew: true },
    ]);
    setShowAllCompanies(true);
    setErrors((prev) => ({ ...prev, companies: "" }));
  };

  // ---- Users --------------------------------------------------------------

  const memberCompanyOptions = companies
    .filter((row) => row.company_id)
    .map((row) => ({ id: row.company_id, companyname: row.company_name }));

  // Users the "Select Users" panel can offer: everyone eligible who isn't
  // already in the list. The panel itself limits them to the assigned
  // companies.
  const pickableUsers = useMemo(() => {
    return userPool.filter((user) => !isGroupMember(user, members));
  }, [members, userPool]);

  // The Company shown for a user is the company they actually belong to
  // (2026-10-07, user request). Users added now are also saved with that
  // company; the saved company is the fallback here for older members
  // whose own company is not known.
  const memberCompanyName = (row) => row.home_company || row.company || "";

  const updateMember = (key, patch) =>
    setMembers((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const addPickedUsers = (picked) => {
    // Each user is saved with their own company, which need not be one of
    // the companies assigned to this group.
    setMembers((prev) => [
      ...prev,
      ...picked.filter((user, index) => !isGroupMember(user, prev) &&
        !isGroupMember(user, picked.slice(0, index))).map((user) => ({
        key: nextKey(),
        id: membershipUserId(user),
        user_id: membershipUserId(user),
        user_code: user.user_code ?? "",
        // Shown under the name, same as in the Add Users list.
        employee_id: user.employee_id ?? "",
        name: user.name ?? "",
        email: user.email ?? "",
        image_url: user.image_url ?? "",
        company: user.company ?? "",
        company_id: user.company_id ?? null,
        home_company_id: user.home_company_id,
        home_company: user.home_company ?? "",
        // Added users always start on Current Department.
        ...DEFAULT_SCOPE,
        isNew: false,
      })),
    ]);
    setUserPickerOpen(false);
    setShowAllMembers(true);
    setMemberSearch("");
  };

  const removeMembers = (keys) => {
    const drop = new Set(keys);
    setMembers((prev) => prev.filter((row) => !drop.has(row.key)));
    setSelectedKeys((prev) => prev.filter((key) => !drop.has(key)));
  };

  const removeCompanyRow = (row) => {
    setCompanies((prev) => prev.filter((item) => item.key !== row.key));
  };

  // Search in the "All users" bar filters the table by name, user code or
  // company.
  const memberQuery = memberSearch.trim().toLowerCase();
  const filteredMembers = memberQuery
    ? members.filter((row) =>
        `${row.name ?? ""} ${row.employee_id ?? ""} ${row.user_code ?? ""} ${memberCompanyName(row)}`.toLowerCase().includes(memberQuery),
      )
    : members;
  const visibleMembers =
    showAllMembers || filteredMembers.length <= MEMBER_PREVIEW
      ? filteredMembers
      : filteredMembers.slice(0, MEMBER_PREVIEW);
  const visibleCompanies =
    showAllCompanies || companies.length <= COMPANY_PREVIEW
      ? companies
      : companies.slice(0, COMPANY_PREVIEW);

  const selectableKeys = filteredMembers.map((row) => row.key);
  const allSelected =
    selectableKeys.length > 0 && selectableKeys.every((key) => selectedKeys.includes(key));
  const someSelected = selectableKeys.some((key) => selectedKeys.includes(key));

  // ---- Save ---------------------------------------------------------------

  const validateName = () => {
    const trimmed = name.trim();
    let message = "";
    if (!trimmed) message = "Group name is required";
    else if (trimmed.length < 3) message = "Minimum 3 characters required";
    else if (!atleastOneCharacterWithSpace.test(trimmed)) message = "At least one character is required";
    setErrors((prev) => ({ ...prev, name: message }));
    return !message;
  };

  const nameChanged = !isEdit || name.trim() !== (details?.name ?? group?.name ?? "").trim();

  const checkNameUnique = async () => {
    if (!name.trim() || !nameChanged) return true;
    try {
      const res = await customGetAPI(
        `${domain}unique/user-group/?name=${encodeURIComponent(name.trim())}`,
      );
      if (res?.ok && res?.responseDetails?.exists) {
        setErrors((prev) => ({ ...prev, name: "Group name already exists" }));
        return false;
      }
      return Boolean(res?.ok);
    } catch (error) {
      showToast("error", error?.message || "Couldn't verify the group name. Please try again.");
      return false;
    }
  };

  const validateRows = () => {
    const nextRowErrors = {};
    const assignmentPairs = new Set();
    companies.forEach((row) => {
      if (!row.company_id || row.roles.length === 0) {
        nextRowErrors[row.key] = {
          company: row.company_id ? "" : "Select a company",
          roles: row.roles.length ? "" : "Select at least one role",
        };
      }
      row.roles.forEach((role) => {
        const pair = `${row.company_id}::${role.role_id}`;
        if (assignmentPairs.has(pair)) nextRowErrors[row.key] = { roles: "This company-role assignment is duplicated" };
        assignmentPairs.add(pair);
      });
    });
    const memberPairs = new Set();
    members.forEach((row) => {
      if (!row.id || !hasValidDepartmentScope(row) || (row.scope === "additional" &&
        !row.departments.every((department) => departmentList.some((option) => String(option.id) === String(department.id))))) {
        nextRowErrors[row.key] = {
          user: row.id ? "" : "Select a user",
          scope: !hasValidDepartmentScope(row) ? "Choose a department scope and select departments when required"
              : "Select valid departments from the department list",
        };
      }
      if (memberPairs.has(String(membershipUserId(row)))) nextRowErrors[row.key] = { scope: "This user is already in the group" };
      memberPairs.add(String(membershipUserId(row)));
    });
    setRowErrors(nextRowErrors);
    if (Object.keys(nextRowErrors).length) {
      setShowAllCompanies(true);
      setShowAllMembers(true);
      setMemberSearch("");
    }
    const companyMessage = companies.length ? "" : "Add at least one company.";
    setErrors((prev) => ({ ...prev, companies: companyMessage }));
    return Object.keys(nextRowErrors).length === 0 && !companyMessage && !departmentsLoading && !departmentsError;
  };

  const save = async () => {
    if (saving) return;
    const nameValid = validateName();
    const rowsValid = validateRows();
    if (!nameValid || !rowsValid) return;
    setSaving(true);
    try {
      if (!(await checkNameUnique())) return;
      // One assignment per company-role pair: a company with two roles is
      // sent as two entries for the same company_id.
      const assignments = companies.flatMap((row) =>
        row.roles.map((role) => ({ company_id: row.company_id, role_id: role.role_id })),
      );
      const body = {
        name: name.trim(),
        description: description.trim(),
        assignments,
        users: buildGroupUsersPayload(members),
      };
      if (supportsStatus) {
        if (details?.is_active !== undefined && details?.status === undefined) {
          body.is_active = status === "Active";
        } else {
          body.status = status;
        }
      }
      const response = isEdit
        ? await customAPI(`${domain}create-assign/user-group/`, "PUT", { id: group?.id, ...body })
        : await customAPI(`${domain}create-assign/user-group/`, "POST", body);
      if (response?.ok) {
        notifyAccessChanged();
        showToast("success", isEdit ? "User group updated." : "User group created.");
        onSaved?.();
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  // ---- Render -------------------------------------------------------------

  const header = (
    <Mui.Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
      {isEdit ? (
        <GroupIcon group={group} size={56} />
      ) : (
        <Mui.Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "10px",
            backgroundColor: "#FCE7F3",
            color: "#DB2777",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <GroupsRounded sx={{ fontSize: 30 }} />
        </Mui.Box>
      )}
      <Mui.Box sx={{ minWidth: 0 }}>
        <Mui.Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
          <Mui.Typography
            sx={{ fontSize: 22, fontWeight: 700, fontFamily: designFontFamily, color: designColors.slate800 }}
          >
            {isEdit ? "Edit User Group" : "Add User Group"}
          </Mui.Typography>
          {isEdit ? statusChip(status) : null}
        </Mui.Stack>
        <Mui.Typography sx={{ mt: 0.25, fontSize: 13.5, color: designColors.slate600 }}>
          {isEdit
            ? "Update group details, company-role assignments, and group membership."
            : "Enter group details, assign company roles, and add users."}
        </Mui.Typography>
      </Mui.Box>
    </Mui.Stack>
  );

  // While the Add Users picker is open, the drawer's sticky footer carries
  // its actions, so "Add Selected Users" is always visible however long the
  // employee list is.
  const pickerFooter = (
    <Mui.Stack direction="row" spacing={1.5} alignItems="center">
      <Mui.Typography sx={{ flex: 1, fontSize: 13, color: designColors.slate600 }}>
        {pickedUsers.length} user{pickedUsers.length === 1 ? "" : "s"} selected
      </Mui.Typography>
      <Mui.Button
        sx={roleAndGroupUi.secondaryButton}
        onClick={() => setUserPickerOpen(false)}
      >
        Cancel
      </Mui.Button>
      <Mui.Button
        sx={roleAndGroupUi.primaryButton}
        disabled={!pickedUsers.length}
        onClick={() => addPickedUsers(pickedUsers)}
      >
        Add Selected Users
      </Mui.Button>
    </Mui.Stack>
  );

  const footer = userPickerOpen ? pickerFooter : (
    <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end">
      <Mui.Button sx={roleAndGroupUi.secondaryButton} onClick={onClose}>
        Cancel
      </Mui.Button>
      <Mui.Button
        sx={roleAndGroupUi.primaryButton}
        onClick={save}
        disabled={saving || !ready || departmentsLoading || Boolean(departmentsError)}
        aria-busy={saving}
      >
        {saving ? (
          <Mui.CircularProgress size={20} color="inherit" />
        ) : isEdit ? (
          "Save Changes"
        ) : (
          "Create Group"
        )}
      </Mui.Button>
    </Mui.Stack>
  );

  const statusSelect = (
    <Mui.Select
      fullWidth
      size="small"
      value={status}
      disabled={!supportsStatus}
      onChange={(event) => setStatus(event.target.value)}
      renderValue={(value) => (
        <Mui.Stack direction="row" spacing={1} alignItems="center">
          <Mui.Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: value === "Active" ? "#16A34A" : designColors.slate400,
            }}
          />
          <span>{value}</span>
        </Mui.Stack>
      )}
      sx={{
        minHeight: 48,
        borderRadius: "12px",
        fontSize: 14,
        fontWeight: 600,
        fontFamily: designFontFamily,
        color: status === "Active" ? "#15803D" : designColors.slate600,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: designColors.borderInput },
      }}
    >
      <Mui.MenuItem value="Active">Active</Mui.MenuItem>
      <Mui.MenuItem value="Inactive">Inactive</Mui.MenuItem>
    </Mui.Select>
  );

  return (
    <GlobalSidePanel
      open
      onClose={onClose}
      header={header}
      breadcrumb={[
        "Administration",
        { label: "User Groups", onClick: onClose },
        isEdit ? "Edit User Group" : "Add User Group",
      ]}
      footer={footer}
      expanded={panelExpanded}
      onToggleExpand={() => setPanelExpanded((prev) => !prev)}
      bodySx={{ backgroundColor: designColors.pageBg }}
    >
      {!ready ? (
        detailsError ? <Mui.Alert severity="error" sx={{ m: 2 }}>{detailsError}</Mui.Alert> : <Loader info="Loading..." minHeight="60vh" />
      ) : (
        <Mui.Stack spacing={2} sx={{ p: { xs: 2, sm: 2.5 } }}>
          {departmentsError ? <Mui.Alert severity="error">{departmentsError}</Mui.Alert> : null}
          {/* Group details */}
          <Mui.Box sx={sectionCardSx}>
            <SectionTitle icon={<ShieldRounded />} iconColor="#DB2777" title="Group Details" />
            <Mui.Grid container spacing={2}>
              <Mui.Grid item xs={12} sm={isEdit ? 8 : 12}>
                <GlobalFieldLabel>
                  Group Name <Mui.Box component="span" sx={{ color: "#DC2626" }}>*</Mui.Box>
                </GlobalFieldLabel>
                <Mui.TextField
                  fullWidth
                  size="small"
                  value={name}
                  placeholder="Enter user group name"
                  onChange={(event) => {
                    const next = event.target.value;
                    if (specialCharacterRestrict.test(next) && next.length <= NAME_MAX) {
                      setName(next);
                      if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
                    }
                  }}
                  onBlur={checkNameUnique}
                  error={Boolean(errors.name)}
                  sx={fieldSx}
                />
                <Mui.Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Mui.Box>{errorText(errors.name)}</Mui.Box>
                  <Mui.Typography sx={{ mt: 0.5, fontSize: 12, color: designColors.slate600 }}>
                    {name.length}/{NAME_MAX}
                  </Mui.Typography>
                </Mui.Stack>
              </Mui.Grid>
              {isEdit ? (
                <Mui.Grid item xs={12} sm={4}>
                  <GlobalFieldLabel>Status</GlobalFieldLabel>
                  {statusSelect}
                </Mui.Grid>
              ) : null}
              <Mui.Grid item xs={12}>
                <GlobalFieldLabel>Description</GlobalFieldLabel>
                {/* Compact: two rows, no extra bottom padding; the counter sits
                    under the field like Group Name's instead of inside it. */}
                <Mui.TextField
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={3}
                  value={description}
                  placeholder="Describe the purpose of this user group"
                  onChange={(event) => {
                    if (event.target.value.length <= DESCRIPTION_MAX) setDescription(event.target.value);
                  }}
                  sx={{
                    ...fieldSx,
                    "& .MuiOutlinedInput-root": {
                      ...roleAndGroupUi.textField["& .MuiOutlinedInput-root"],
                      alignItems: "flex-start",
                      py: 1.25,
                    },
                  }}
                />
                <Mui.Typography sx={{ mt: 0.5, textAlign: "right", fontSize: 12, color: designColors.slate600 }}>
                  {description.length}/{DESCRIPTION_MAX}
                </Mui.Typography>
              </Mui.Grid>
            </Mui.Grid>
          </Mui.Box>

          {/* Companies & roles */}
          <Mui.Box sx={sectionCardSx}>
            <SectionTitle
              icon={<ApartmentRounded />}
              iconColor="#7C3AED"
              title="Companies & Role Assignment"
              subtitle="Assign roles in each company for this user group."
              action={
                <Mui.Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addCompany}
                  disabled={availableCompanies(null).length === 0}
                  sx={addSectionButtonSx}
                >
                  Add Company
                </Mui.Button>
              }
            />
            <Mui.Box sx={companyListSx}>
              {companies.length === 0 ? (
                <Mui.Typography
                  sx={{ py: 3, px: 2, textAlign: "center", fontSize: 13.5, color: designColors.slate600 }}
                >
                  No companies yet. Use Add Company to assign roles.
                </Mui.Typography>
              ) : (
                visibleCompanies.map((row) => (
                  <Mui.Box key={row.key} sx={companyRowSx}>
                    <Mui.Box sx={{ width: { xs: "calc(100% - 48px)", sm: 160 }, flexShrink: 0, minWidth: 0 }}>
                      {/* Same company dropdown for every row, in Add and Edit alike
                          (2026-10-01, user request) -- Edit used to show saved
                          rows as a read-only avatar + name. */}
                      <>
                          <CustomAutoComplete
                            dataList={availableCompanies(row.key).map((company) => ({
                              ...company,
                              label: company?.companyname ?? "",
                            }))}
                            selectedValue={
                              row.company_id ? { id: row.company_id, label: row.company_name } : null
                            }
                            updateDataFunction={(event, value) => {
                              updateCompany(row.key, {
                                company_id: value?.id ?? null,
                                company_name: value?.companyname ?? value?.label ?? "",
                              });
                              clearRowError(row.key, "company");
                            }}
                            placeHolder="Select company"
                            disablePortal={false}
                          />
                          {errorText(rowErrors[row.key]?.company)}
                      </>
                    </Mui.Box>
                    <Mui.Box
                      sx={{
                        order: { xs: 3, sm: 0 },
                        flex: { xs: "1 1 100%", sm: "1 1 auto" },
                        minWidth: 0,
                      }}
                    >
                      <RoleChipSelect
                        options={roleOptions}
                        value={row.roles}
                        hasError={Boolean(rowErrors[row.key]?.roles)}
                        onChange={(roles) => {
                          updateCompany(row.key, { roles });
                          clearRowError(row.key, "roles");
                        }}
                      />
                      {errorText(rowErrors[row.key]?.roles)}
                    </Mui.Box>
                    <Mui.IconButton
                      aria-label={`Remove ${row.company_name || "company"}`}
                      onClick={() => removeCompanyRow(row)}
                      sx={{ ...deleteButtonSx, flexShrink: 0, ml: { xs: "auto", sm: 0 } }}
                    >
                      <DeleteOutline fontSize="small" />
                    </Mui.IconButton>
                  </Mui.Box>
                ))
              )}
            </Mui.Box>
            {companies.length > COMPANY_PREVIEW ? (
              <MoreRow
                expanded={showAllCompanies}
                hiddenCount={companies.length - COMPANY_PREVIEW}
                noun={companies.length - COMPANY_PREVIEW === 1 ? "company" : "companies"}
                onToggle={() => setShowAllCompanies((prev) => !prev)}
              />
            ) : null}
            {errorText(errors.companies)}
          </Mui.Box>

          {/* Users */}
          <Mui.Box sx={userPickerOpen ? pickerActiveCardSx : sectionCardSx}>
            <SectionTitle
              icon={userPickerOpen ? null : <GroupsRounded />}
              title={userPickerOpen ? "Select Users" : "Users in this group"}
              subtitle={
                userPickerOpen
                  ? "Pick users to add, then choose Add Selected Users."
                  : "Add or remove users and set their department access."
              }
              action={
                userPickerOpen ? null : (
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  placement="top"
                  title={memberCompanyOptions.length ? "" : "Add a company first -- users get their group access under an assigned company."}
                >
                  <span>
                    <Mui.Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={() => setUserPickerOpen(true)}
                      disabled={memberCompanyOptions.length === 0}
                      sx={addSectionButtonSx}
                    >
                      Add Users
                    </Mui.Button>
                  </span>
                </Mui.Tooltip>
                )
              }
            />
            {/* Add Users renders the picker here, in this same panel, in place
                of the users table (2026-10-01, user request -- no second side
                panel). "Add Selected Users" or Cancel brings the table back. */}
            {userPickerOpen ? (
              <UserGroupUserPicker
                open
                inline
                onSelectionChange={setPickedUsers}
                companies={memberCompanyOptions}
                sourceCompanies={companies_}
                users={pickableUsers}
                members={members}
                onClose={() => setUserPickerOpen(false)}
                onAdd={addPickedUsers}
              />
            ) : (
            <>
            {/* Always-visible scope bar: Done in the dropdown applies the scope to
                the ticked users, or to every user when none are ticked. */}
            <Mui.Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{
                mb: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: "10px",
                backgroundColor: "#ECFAF6",
                border: "1px solid #CDEDE4",
              }}
            >
              <Mui.Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0F766E", whiteSpace: "nowrap" }}>
                {selectedKeys.length ? `${selectedKeys.length} selected` : "All users"}
              </Mui.Typography>
              <Mui.Box sx={{ width: { xs: "100%", sm: 240 } }}>
                <DepartmentMultiSelect
                  showDepartmentNames
                  loading={departmentsLoading}
                  error={departmentsError}
                  label={
                    selectedKeys.length
                      ? "Set department access for selected users"
                      : "Set department access for all users"
                  }
                  options={departmentList}
                  value={bulkScope.departments}
                  scope={bulkScope.scope}
                  onChange={(departments, scope) => {
                    setBulkScope({ departments, scope });
                    const chosen = selectedKeys.length ? new Set(selectedKeys) : null;
                    const applies = (key) => !chosen || chosen.has(key);
                    setMembers((prev) =>
                      prev.map((row) => (applies(row.key) ? { ...row, departments, scope } : row)),
                    );
                    setRowErrors((prev) =>
                      Object.fromEntries(
                        Object.entries(prev).map(([key, value]) =>
                          applies(key) ? [key, { ...value, scope: "" }] : [key, value],
                        ),
                      ),
                    );
                  }}
                />
              </Mui.Box>
              {/* Spacer pushes the right-side control to the far right (the
                  Stack's spacing margin overrides an ml:auto on the item). */}
              <Mui.Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }} />
              {/* Right side: user search while nothing is ticked; once users
                  are ticked it gives way to Remove selected. */}
              {selectedKeys.length ? (
                <Mui.Button
                  startIcon={<DeleteOutline />}
                  onClick={() => removeMembers(selectedKeys)}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap", textTransform: "none", fontWeight: 700, color: "#DC2626" }}
                >
                  Remove selected
                </Mui.Button>
              ) : (
                <Mui.TextField
                  size="small"
                  value={memberSearch}
                  placeholder="Search users..."
                  onChange={(event) => setMemberSearch(event.target.value)}
                  inputProps={{ "aria-label": "Search users in this group" }}
                  InputProps={{
                    startAdornment: (
                      <Mui.InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: designColors.slate500 }} />
                      </Mui.InputAdornment>
                    ),
                  }}
                  sx={{
                    ...autocompleteSx,
                    flex: { sm: "1 1 160px" },
                    minWidth: 0,
                    maxWidth: { sm: 220 },
                  }}
                />
              )}
            </Mui.Stack>
            <Mui.TableContainer sx={tableBoxSx}>
              {/* No minWidth: the columns fit the 620px drawer (Name takes the
                  remaining space and truncates), so there is no horizontal
                  scroll. The user code sits under the name instead of in its
                  own column. */}
              <Mui.Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                <Mui.TableHead>
                  <Mui.TableRow>
                    <Mui.TableCell sx={{ ...headCellSx, width: 44, px: 1 }}>
                      <Mui.Checkbox
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        disabled={selectableKeys.length === 0}
                        onChange={(event) =>
                          setSelectedKeys((prev) =>
                            event.target.checked
                              ? [...new Set([...prev, ...selectableKeys])]
                              : prev.filter((key) => !selectableKeys.includes(key)),
                          )
                        }
                        sx={tealCheckboxSx}
                        inputProps={{ "aria-label": "Select all users" }}
                      />
                    </Mui.TableCell>
                    <Mui.TableCell sx={headCellSx}>Name</Mui.TableCell>
                    <Mui.TableCell sx={{ ...headCellSx, ...companyColumnSx(panelExpanded) }}>Company</Mui.TableCell>
                    <Mui.TableCell sx={{ ...headCellSx, width: departmentColumnWidth(panelExpanded) }}>
                      Department Access
                    </Mui.TableCell>
                    <Mui.TableCell sx={{ ...headCellSx, width: 44, px: 0.5 }} />
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {filteredMembers.length === 0 ? (
                    <Mui.TableRow>
                      <Mui.TableCell colSpan={5} sx={{ ...bodyCellSx, py: 3, textAlign: "center", color: designColors.slate600 }}>
                        {members.length === 0
                          ? "No users yet. Use Add Users to add people to this group."
                          : "No users match your search."}
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : (
                    visibleMembers.map((row) => {
                      return (
                        <Mui.TableRow key={row.key} sx={{ "&:last-child td": { borderBottom: "none" } }}>
                          <Mui.TableCell sx={{ ...bodyCellSx, px: 1 }}>
                            <Mui.Checkbox
                              checked={selectedKeys.includes(row.key)}
                              onChange={(event) =>
                                setSelectedKeys((prev) =>
                                  event.target.checked
                                    ? [...prev, row.key]
                                    : prev.filter((key) => key !== row.key),
                                )
                              }
                              sx={tealCheckboxSx}
                              inputProps={{ "aria-label": `Select ${row.name || "user"}` }}
                            />
                          </Mui.TableCell>
                          <Mui.TableCell sx={bodyCellSx}>
                            <Mui.Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                              <UserAvatar name={row.name} image={row.image_url} />
                              <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
                                {/* Shared truncation tooltip: the styled tooltip shows
                                    only when the text is actually cut off. */}
                                <Mui.Typography component="div" sx={{ fontSize: 13.5, fontWeight: 600 }}>
                                  <TruncatedTableValue value={row.name || "-"} />
                                </Mui.Typography>
                                <Mui.Typography component="div" sx={{ fontSize: 12, color: designColors.slate600 }}>
                                  <TruncatedTableValue
                                    value={[row.employee_id || row.user_code || "-", memberCompanyName(row)].filter(Boolean).join(" · ")}
                                  >
                                    {row.employee_id || row.user_code || "-"}
                                    <Mui.Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                                      {memberCompanyName(row) ? ` · ${memberCompanyName(row)}` : ""}
                                    </Mui.Box>
                                  </TruncatedTableValue>
                                </Mui.Typography>
                              </Mui.Box>
                            </Mui.Stack>
                          </Mui.TableCell>
                          <Mui.TableCell sx={{ ...bodyCellSx, ...companyColumnSx(panelExpanded) }}>
                            <Mui.Typography
                              component="div"
                              sx={{ fontSize: 13.5, color: memberCompanyName(row) ? designColors.slate800 : designColors.slate400 }}
                            >
                              <TruncatedTableValue value={memberCompanyName(row) || "-"} />
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell sx={bodyCellSx}>
                            <DepartmentMultiSelect
                              showDepartmentNames
                              loading={departmentsLoading}
                              error={departmentsError}
                              label={`Department access for ${row.name || "user"}`}
                              options={departmentList}
                              value={row.departments || []}
                              scope={row.scope}
                              onChange={(departments, scope) => {
                                updateMember(row.key, { departments, scope });
                                clearRowError(row.key, "scope");
                              }}
                            />
                            {errorText(rowErrors[row.key]?.scope)}
                          </Mui.TableCell>
                          <Mui.TableCell sx={{ ...bodyCellSx, px: 0.5, textAlign: "center" }}>
                            <Mui.IconButton
                              aria-label={`Remove ${row.name || "user"}`}
                              onClick={() => removeMembers([row.key])}
                              sx={deleteButtonSx}
                            >
                              <DeleteOutline fontSize="small" />
                            </Mui.IconButton>
                          </Mui.TableCell>
                        </Mui.TableRow>
                      );
                    })
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
            {filteredMembers.length > MEMBER_PREVIEW ? (
              <MoreRow
                expanded={showAllMembers}
                hiddenCount={filteredMembers.length - MEMBER_PREVIEW}
                noun={filteredMembers.length - MEMBER_PREVIEW === 1 ? "user" : "users"}
                onToggle={() => setShowAllMembers((prev) => !prev)}
              />
            ) : null}
            </>
            )}
          </Mui.Box>
        </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};
